import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  Search, 
  Download,
  RefreshCw,
  Calendar,
  User,
  FileText,
  Bell,
  XCircle,
  Edit,
  ExternalLink,
  Loader2
} from 'lucide-react';

const DBSTrackingDashboard = () => {
  const { api, user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, expiring, expired
  
  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    dbs_number: '',
    issue_date: '',
    expiry_date: '',
    update_service_status: 'not_subscribed'
  });
  const [saving, setSaving] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(null);

  // Check if user has access
  const hasAccess = ['director', 'managing_director', 'hr', 'admin', 'clinical_lead'].includes(user?.role) || 
                    user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  const fetchRecords = useCallback(async () => {
    if (!hasAccess) return;
    
    try {
      const response = await api.get('/dbs-tracking/all');
      setRecords(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch DBS records:', error);
      toast.error('Failed to load DBS records');
    } finally {
      setLoading(false);
    }
  }, [api, hasAccess]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSendReminders = async () => {
    try {
      const response = await api.post('/dbs-tracking/send-renewal-reminders');
      toast.success(`Sent ${response.data.total_sent} reminder notifications`);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    }
  };

  const handleSyncFromAttachments = async () => {
    try {
      const response = await api.post('/dbs-tracking/sync-from-attachments');
      toast.success(`Synced ${response.data.synced_count} DBS records from attachments`);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync');
    }
  };

  const handleCleanupDuplicates = async () => {
    try {
      const response = await api.post('/dbs-tracking/cleanup-duplicates');
      toast.success(`Cleaned up ${response.data.duplicates_removed} duplicate records`);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup');
    }
  };

  // Open edit dialog
  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setEditForm({
      dbs_number: record.dbs_number || '',
      issue_date: record.issue_date || '',
      expiry_date: record.expiry_date || '',
      update_service_status: record.update_service_status || 'not_subscribed'
    });
    setEditDialogOpen(true);
  };

  // Save edited record
  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    
    setSaving(true);
    try {
      await api.put(`/dbs-tracking/${editingRecord.id}`, editForm);
      toast.success('DBS record updated successfully');
      setEditDialogOpen(false);
      setEditingRecord(null);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };

  // Check DBS Update Service status
  const handleCheckUpdateService = async (recordId) => {
    setCheckingStatus(recordId);
    try {
      const response = await api.post(`/dbs-tracking/${recordId}/check-update-service`);
      toast.success(`DBS Update Service status: ${response.data.status}`);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check status');
    } finally {
      setCheckingStatus(null);
    }
  };

  const getStatusBadge = (record) => {
    // If subscribed to Update Service and status is current
    if (record.update_service_status === 'current') {
      return <Badge className="bg-green-600 text-white"><CheckCircle className="w-3 h-3 mr-1" /> CURRENT (Update Service)</Badge>;
    }
    
    // If subscribed but status changed
    if (record.update_service_status === 'changed') {
      return <Badge className="bg-red-600 text-white"><AlertTriangle className="w-3 h-3 mr-1" /> CHANGED - New Check Required</Badge>;
    }
    
    // Check expiry date based status
    if (!record.expiry_date || record.expiry_date === '-' || record.expiry_date === '') {
      // No expiry date - check if subscribed to Update Service
      if (record.update_service_status === 'subscribed') {
        return <Badge className="bg-blue-500 text-white"><Shield className="w-3 h-3 mr-1" /> Update Service Active</Badge>;
      }
      return <Badge variant="outline" className="text-amber-600 border-amber-300"><Clock className="w-3 h-3 mr-1" /> Needs Update</Badge>;
    }
    
    if (record.days_until_expiry === null || record.days_until_expiry === undefined) {
      return <Badge variant="outline" className="text-slate-500">Check Required</Badge>;
    }
    if (record.days_until_expiry < 0) {
      return <Badge className="bg-red-600 text-white"><XCircle className="w-3 h-3 mr-1" /> EXPIRED</Badge>;
    }
    if (record.days_until_expiry <= 30) {
      return <Badge className="bg-red-500 text-white animate-pulse"><AlertTriangle className="w-3 h-3 mr-1" /> URGENT ({record.days_until_expiry}d)</Badge>;
    }
    if (record.days_until_expiry <= 60) {
      return <Badge className="bg-amber-500 text-white"><Clock className="w-3 h-3 mr-1" /> WARNING ({record.days_until_expiry}d)</Badge>;
    }
    return <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" /> OK ({record.days_until_expiry}d)</Badge>;
  };

  // Get Update Service status badge
  const getUpdateServiceBadge = (status) => {
    switch(status) {
      case 'subscribed':
        return <Badge variant="outline" className="text-blue-600 border-blue-300">Subscribed</Badge>;
      case 'current':
        return <Badge className="bg-green-100 text-green-700">Current</Badge>;
      case 'changed':
        return <Badge className="bg-red-100 text-red-700">Changed</Badge>;
      default:
        return <Badge variant="outline" className="text-slate-400">Not Subscribed</Badge>;
    }
  };

  const filteredRecords = records.filter(record => {
    const matchesSearch = record.staff_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          record.dbs_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'expiring') {
      return matchesSearch && record.days_until_expiry !== null && record.days_until_expiry >= 0 && record.days_until_expiry <= 60;
    }
    if (filter === 'expired') {
      return matchesSearch && record.days_until_expiry !== null && record.days_until_expiry < 0;
    }
    return matchesSearch;
  });

  const stats = {
    total: records.length,
    expired: records.filter(r => r.days_until_expiry !== null && r.days_until_expiry < 0).length,
    urgent: records.filter(r => r.days_until_expiry !== null && r.days_until_expiry >= 0 && r.days_until_expiry <= 30).length,
    warning: records.filter(r => r.days_until_expiry !== null && r.days_until_expiry > 30 && r.days_until_expiry <= 60).length,
    ok: records.filter(r => r.days_until_expiry !== null && r.days_until_expiry > 60).length
  };

  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
          <p className="text-slate-500 mt-2">Loading DBS records...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <Shield className="w-6 h-6" />
            DBS Certificate Tracking
          </h1>
          <p className="text-slate-500">Monitor and track staff DBS certificate expiry dates</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchRecords}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {user?.email?.toLowerCase() === 'admin@icrcl.co.uk' && (
            <>
              <Button variant="outline" onClick={handleSyncFromAttachments} title="Sync DBS records from existing attachments">
                <Download className="w-4 h-4 mr-2" />
                Sync from Uploads
              </Button>
              <Button variant="outline" onClick={handleCleanupDuplicates} title="Remove duplicate DBS entries">
                <CheckCircle className="w-4 h-4 mr-2" />
                Cleanup Duplicates
              </Button>
              <Button onClick={handleSendReminders} className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
                <Bell className="w-4 h-4 mr-2" />
                Send Reminders
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:border-slate-400" onClick={() => setFilter('all')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
            <p className="text-sm text-slate-500">Total Records</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-red-400 ${filter === 'expired' ? 'border-red-500' : ''}`} onClick={() => setFilter('expired')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
            <p className="text-sm text-red-500">Expired</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-red-400 ${filter === 'expiring' ? 'border-red-500' : ''}`} onClick={() => setFilter('expiring')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-red-500">{stats.urgent}</div>
            <p className="text-sm text-red-400">Urgent (≤30d)</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-amber-400" onClick={() => setFilter('expiring')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-500">{stats.warning}</div>
            <p className="text-sm text-amber-400">Warning (≤60d)</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-green-400" onClick={() => setFilter('all')}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{stats.ok}</div>
            <p className="text-sm text-green-500">Up to Date</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or DBS number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
              <Button 
                variant={filter === 'expiring' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('expiring')}
                className={filter === 'expiring' ? 'bg-amber-500 hover:bg-amber-600' : ''}
              >
                Expiring Soon
              </Button>
              <Button 
                variant={filter === 'expired' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('expired')}
                className={filter === 'expired' ? 'bg-red-500 hover:bg-red-600' : ''}
              >
                Expired
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No DBS records found</p>
              <p className="text-sm text-slate-400 mt-1">
                DBS certificates will appear here when staff upload them
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff Name</TableHead>
                  <TableHead>DBS Number</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Update Service</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {record.staff_name}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {record.dbs_number || <span className="text-amber-500">Not entered</span>}
                    </TableCell>
                    <TableCell>
                      {record.issue_date ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {new Date(record.issue_date).toLocaleDateString('en-GB')}
                        </div>
                      ) : <span className="text-amber-500">-</span>}
                    </TableCell>
                    <TableCell>
                      {record.expiry_date ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {new Date(record.expiry_date).toLocaleDateString('en-GB')}
                        </div>
                      ) : <span className="text-amber-500">-</span>}
                    </TableCell>
                    <TableCell>
                      {getUpdateServiceBadge(record.update_service_status)}
                    </TableCell>
                    <TableCell>{getStatusBadge(record)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEditRecord(record)}
                          title="Edit DBS details"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {record.dbs_number && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleCheckUpdateService(record.id)}
                            disabled={checkingStatus === record.id}
                            title="Check Update Service status"
                          >
                            {checkingStatus === record.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open('https://www.gov.uk/dbs-update-service', '_blank')}
                          title="Open gov.uk DBS Update Service"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit DBS Record Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1e3a5f]" />
              Edit DBS Record
            </DialogTitle>
            <DialogDescription>
              Update DBS certificate details for {editingRecord?.staff_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dbs_number">DBS Certificate Number *</Label>
              <Input
                id="dbs_number"
                placeholder="e.g., 001234567890"
                value={editForm.dbs_number}
                onChange={(e) => setEditForm(prev => ({ ...prev, dbs_number: e.target.value }))}
              />
              <p className="text-xs text-slate-500">12-digit number found on the DBS certificate</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="issue_date">Issue Date</Label>
                <Input
                  id="issue_date"
                  type="date"
                  value={editForm.issue_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, issue_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiry_date">Expiry Date</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={editForm.expiry_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                />
                <p className="text-xs text-slate-500">Usually 3 years from issue</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>DBS Update Service Status</Label>
              <Select 
                value={editForm.update_service_status} 
                onValueChange={(value) => setEditForm(prev => ({ ...prev, update_service_status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_subscribed">Not Subscribed</SelectItem>
                  <SelectItem value="subscribed">Subscribed (Active)</SelectItem>
                  <SelectItem value="current">Current (No Changes)</SelectItem>
                  <SelectItem value="changed">Changed (New Check Required)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Staff can register at{' '}
                <a 
                  href="https://www.gov.uk/dbs-update-service" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  gov.uk/dbs-update-service
                </a>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">About DBS Update Service</p>
                  <p className="text-blue-600 mt-1">
                    If the staff member is subscribed to the DBS Update Service, their certificate 
                    doesn&apos;t expire and can be checked online. Set status to &quot;Current&quot; after verifying on gov.uk.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DBSTrackingDashboard;
